<template>
  <div>
    <v-layout class="title" align-baseline justify-space-between>
      <v-flex xs2>
        {{ $t('packageShipmentDetails') }}:
      </v-flex>
      <v-flex md2>
        <v-autocomplete
          hide-details
          :items="templates"
          return-object
          :label="$t('template')"
          item-text="name"
          @change="onSelectTemplate"></v-autocomplete>
      </v-flex>
      <v-flex xs2>
        <v-menu
          :close-on-content-click="false"
          v-model="shipDateMenu"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template v-slot:activator="data">
            <v-text-field
              hide-details
              :value="shipDate"
              :label="$t('shipDate')"
              readonly
              v-on="data.on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="shipDate" @input="shipDateMenu = false"></v-date-picker>
        </v-menu>
      </v-flex>
      <v-flex xs3>
        <v-autocomplete
          v-if="isAutoGetRate && autoGetRate"
          v-model="carriers"
          :items="allCarriers"
          :label="$t('autoGetRate')"
          :rules="[arrayLengthRule()]"
          multiple
         >
         <template v-slot:prepend-item>
          <v-list-tile
            ripple
            @mousedown.prevent
            @click="selectAll"
          >
            <v-list-tile-action>
              <v-icon color="#224855">{{ allIcon }}</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('selectAll') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:append>
            <v-checkbox
            :style="{height: '10px', marginTop: '-10px'}"
            v-model="autoGetRate"
            :label="$t('autoGetRate')"
            ></v-checkbox>
          </template>
        </v-autocomplete>
        <v-select
          v-else
          hide-details
          :items="shippingServices"
          item-text="text"
          item-value="value"
          :menu-props="{returnValue:'value'}"
          v-model="shippingService"
          :rules="[fieldIsRequired('service')]"
          :label="$t('shippingService')"
          @changeShippingService="e => $emit('changeShippingService', e)"
        >
          <template v-slot:item="data">
            <template v-if="isObject(data.item)">
              <v-list-tile-content v-text="data.item"></v-list-tile-content>
            </template>
            <template v-else>
              <v-list-tile-content>
                <v-list-tile-title>
                  {{data.item.text}}
                </v-list-tile-title>
              </v-list-tile-content>
            </template>
          </template>
          <template v-if="isAutoGetRate" v-slot:append>
            <v-checkbox
            :style="{height: '10px', marginTop: '-10px'}"
            v-model="autoGetRate"
            :label="$t('autoGetRate')"
            ></v-checkbox>
          </template>
        </v-select>
      </v-flex>
      <v-flex xs2>
        <v-select
          class="table-item"
          hide-details
          :label="$t('labelStockType')"
          :items="labelStockTypes"
          item-text="text"
          :menu-props="{returnValue:'value'}"
          v-model="labelStockType"/>
      </v-flex>
    </v-layout>
    <v-layout justify-space-between>
      <v-flex xs3>
        <v-autocomplete
          hide-details
          :items="[{
            name: $t('documents'),
            value: 'documents'
          }, {
            name: $t('Products/Commodities'),
            value: 'commodities'
          }]"
          v-model="packageContent"
          :label="$t('packageContents')"
          item-text="name"
          item-value="value"
          readonly></v-autocomplete>
      </v-flex>
      <v-flex xs2>
        <component
          :is="isImportToUsByDHL ? 'v-combobox' : 'v-autocomplete'"
          v-model="shipmentPurpose"
          :items="shipmentPurposeItems"
          :label="$t('shipmentPurpose')"
          :rules="[shipmentPurposeValid(isGetRateAction)]"
          :class="isAutoGetRate && autoGetRate && isGetRateAction ? '' : 'required_field'"
          hide-details
          item-text="name"
          item-value="value"
          :return-object="false"
        />
      </v-flex>
      <v-flex xs3>
        <v-autocomplete
          :label="$t('signature')"
          :items="signatureOptions"
          item-text="text"
          persistent-hint
          :hide-details="isImportToUsByDHL"
          :hint="isImportToUsByDHL ? '' : $t('signatureIsRequiredForFedExInternationalShipment')"
          :menu-props="{returnValue:'value'}"
          v-model="signature"/>
      </v-flex>
      <v-flex xs3>
        <v-text-field
          v-if="isShowBookingNumber"
          hide-details
          :label="$t('bookingConfirmationNumber')"
          v-model="bookingConfirmationNumber"
          :rules="[
            fieldIsRequired('qty')
          ]"></v-text-field>
        <v-autocomplete
          v-if="isImportToUsByDHL"
          :label="$t('battery')"
          :items="batteries"
          hide-details
          v-model="batteryLabel"/>
      </v-flex>
    </v-layout>
    <v-layout v-if="isImportToUsByDHL" justify-space-between>
      <v-flex xs3>
        <v-text-field
          hide-details
          v-model.number="freightCharges"
          :rules="[v => !v || isNaN(v) && $t('fieldIsNumber', { fieldName: 'This field' })]"
          :label="$t('freightCharges')"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="packages"
      hide-actions
      class="elevation-2 mt-2"
      item-key="_key"
      :expand="true"
    >
      <template v-slot:expand="props">
        <LithiumBatteryDangerousGoods
          v-if="isLithiumBatteryDangerousGoods || (props.item.isLithiumBattery && props.item.lithiumBatteryOptions && props.item.lithiumBatteryOptions.length > 0)"
          :props="props"
          :lithiumBatteryOptionItems="lithiumBatteryOptionItems"
          @applyAll="applyAllLithiumBattery"
        />
      </template>
      <template v-slot:headerCell="props">
        <span v-if="props.header.value === 'action'">
          <v-tooltip top>
            <template v-slot:activator="tooltip">
              <div v-on="tooltip.on">
                <v-btn color="primary" flat small icon @click="addPackage" :disabled="!serviceSetting.enableMWT"><v-icon>add</v-icon></v-btn>
                <v-btn flat small icon color="primary"  @click=" () => {isOpenImportPackagesDialog = true}" :disabled="!serviceSetting.enableMWT">
            <v-icon>cloud_upload</v-icon>
          </v-btn>
              </div>
            </template>
            {{ $t('supportUpTo25Packages') }}
          </v-tooltip>
        </span>
        <span v-else-if="props.header.value === 'weight'" @click="isOz = !isOz">{{ props.header.text }}</span>
        <template v-else-if="props.header.value === 'reference'">
          <v-select
            hide-details
            :label="$t('reference')"
            :items="referenceOptions"
            item-text="text"
            :menu-props="{returnValue:'value'}"
            v-model="reference"
          />
        </template>
        <span v-else>{{ props.header.text }}</span>
      </template>
      <template v-slot:items="props">
        <tr>
          <td class="text-xs-center">
            <v-btn v-if="props.index > 0" color="primary" flat small icon @click="deletePackage(props.item)"><v-icon>delete</v-icon> </v-btn>
          </td>
          <td class="text-xs-left">
            <v-text-field
              class="table-item"
              hide-details
              v-model.number="props.item.qty"
              :disabled="!serviceSetting.enableMWT"
              :rules="qtyRules"></v-text-field>
          </td>
          <td class="text-xs-left">
            <v-text-field
              class="table-item"
              hide-details
              v-model.number="props.item.weightLb"
              :rules="weightRules"
              :suffix="isMeasurementMetric ? 'kg' : (isOz ? 'Oz' : 'Lbs')"></v-text-field>
          </td>
          <td class="text-xs-left">
            <v-layout align-center>
              <v-flex xs4 class="mr-3">
                <v-text-field
                  class="table-item"
                  hide-details
                  v-model.number="props.item.length"
                  :suffix="sizeSuffix"
                  :rules="lengthRules"></v-text-field>
              </v-flex>
              <v-flex xs4 class="mr-3">
                <v-text-field
                  class="table-item"
                  hide-details
                  v-model.number="props.item.width"
                  :suffix="sizeSuffix"
                  :rules="widthRules"></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  class="table-item"
                  hide-details
                  v-model.number="props.item.height"
                  :suffix="sizeSuffix"
                  :rules="heightRules"></v-text-field>
              </v-flex>
              <v-flex xs1>
                <PackagingSelector
                  :packagings="packagings"
                  @select="item => onSelectPackaging(item, props.item)"
                />
              </v-flex>
            </v-layout>
          </td>
          <td class="text-xs-left">
            <v-text-field
              class="table-item"
              hide-details
              :disabled="!serviceSetting.enableInsured"
              v-model.number="props.item.declaredValue"
              :rules="declaredValueRules"></v-text-field>
          </td>
          <td class="text-xs-left">
            <v-text-field
              class="table-item"
              hide-details
              :disabled="serviceSetting.disabledPkgMemo"
              v-model="props.item.memo"></v-text-field>
          </td>
          <td class="text-xs-left" v-if="currentShippingService.enableReferences">
            <v-text-field
              class="table-item"
              hide-details
              :rules="[fieldCharLimit('Reference', currentShippingService.referenceLength || 40)]"
              v-model="props.item.reference"></v-text-field>
          </td>
          <td class="text-xs-center" v-if="isLithiumBatteryDangerousGoods">
            <v-layout align-center style="cursor: pointer;" justify-center :id="`expand-icon-${props.item._key || props.index}`" @click="toggleExpand(props)">
              <v-icon color="primary" class="mr-1">battery_charging_full</v-icon>
              <v-icon>expand_more</v-icon>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
     <ImportPackagesDimensions
    v-model="isOpenImportPackagesDialog"
    :isShow="isOpenImportPackagesDialog"
    @confirmMethod="loadDimensionsData">
  </ImportPackagesDimensions>
  </div>

</template>

<script>
import { cloneDeep, checkRules, timeTools, measurementTools, isObject, toMoney } from '@/utils/tools'
import ImportPackagesDimensions from './ImportPackagesDimensions'
import PackagingSelector from './PackagingSelector'
import LithiumBatteryDangerousGoods from './LithiumBatteryDangerousGoods.vue'

const METAL_CONTAINED = 'METAL_CONTAINED'
const METAL_PACKED = 'METAL_PACKED'
const ION_CONTAINED = 'ION_CONTAINED'
const ION_PACKED = 'ION_PACKED'

const initPackage = {
  height: 0,
  width: 0,
  weightLb: 0,
  length: 0,
  isOz: false,
  declaredValue: 0,
  packaging: 'Default packaging',
  qty: 1,
  memo: '',
  isLithiumBattery: false,
  lithiumBatteryOptions: []
  // isPrintSafetyDocument and signatureCode are now global (in HazardousCommodities)
}
// SALE: 'SALE',
// SAMPLE: 'SAMPLE',
// GIFT: 'GIFT',
// RETURN: 'RETURN',
// REPAIR: 'REPAIR',
// INTERCOMPANYDATA_PERSONALEFFECTS: 'INTERCOMPANYDATA_PERSONALEFFECTS',
const shipmentPurposes = [
  {
    name: 'Sale',
    value: 'SALE'
  },
  {
    name: 'Sample',
    value: 'SAMPLE'
  },
  {
    name: 'Gift',
    value: 'GIFT'
  },
  {
    name: 'Return',
    value: 'RETURN'
  },
  {
    name: 'Repair',
    value: 'REPAIR'
  },
  {
    name: 'Inter company data or personal effects',
    value: 'INTERCOMPANYDATA_PERSONALEFFECTS'
  }
]
// const shipmentPurposeMap = {
//   'FEDEX-INTERNATIONAL': [
//     {
//       name: 'Commercial',
//       value: 'COMMERCIAL'
//     },
//     {
//       name: 'Gift',
//       value: 'GIFT'
//     },
//     {
//       name: 'Not sold',
//       value: 'NOT_SOLD'
//     },
//     {
//       name: 'Personal effects',
//       value: 'PERSONAL_EFFECTS'
//     },
//     {
//       name: 'Repair and return',
//       value: 'REPAIR_AND_RETURN'
//     },
//     {
//       name: 'Sample',
//       value: 'SAMPLE'
//     }
//   ],
//   'FEDEX-IMPORT-INTERNATIONAL': [
//     {
//       name: 'Commercial',
//       value: 'COMMERCIAL'
//     },
//     {
//       name: 'Gift',
//       value: 'GIFT'
//     },
//     {
//       name: 'Not sold',
//       value: 'NOT_SOLD'
//     },
//     {
//       name: 'Personal effects',
//       value: 'PERSONAL_EFFECTS'
//     },
//     {
//       name: 'Repair and return',
//       value: 'REPAIR_AND_RETURN'
//     },
//     {
//       name: 'Sample',
//       value: 'SAMPLE'
//     }
//   ],
//   FEDEX: [
//     {
//       name: 'Commercial',
//       value: 'COMMERCIAL'
//     },
//     {
//       name: 'Gift',
//       value: 'GIFT'
//     },
//     {
//       name: 'Not sold',
//       value: 'NOT_SOLD'
//     },
//     {
//       name: 'Personal effects',
//       value: 'PERSONAL_EFFECTS'
//     },
//     {
//       name: 'Repair and return',
//       value: 'REPAIR_AND_RETURN'
//     },
//     {
//       name: 'Sample',
//       value: 'SAMPLE'
//     }
//   ],
//   // SALE, GIFT, SAMPLE, RETURN, REPAIR, INTERCOMPANYDATA
//   UPS: [
//     {
//       name: 'Commercial',
//       value: 'COMMERCIAL'
//     },
//     {
//       name: 'Gift',
//       value: 'GIFT'
//     },
//     {
//       name: 'Sale',
//       value: 'SALE'
//     },
//     {
//       name: 'Return',
//       value: 'RETURN'
//     },
//     {
//       name: 'Repair',
//       value: 'REPAIR'
//     },
//     {
//       name: 'Inter company data',
//       value: 'INTERCOMPANYDATA'
//     },
//     {
//       name: 'Sample',
//       value: 'SAMPLE'
//     }
//   ]
// }

export default {
  name: 'EeiPackageWidget',
  components: {
    ImportPackagesDimensions,
    PackagingSelector,
    LithiumBatteryDangerousGoods
  },
  data () {
    return {
      memo: '',
      note: '',
      shipDate: this.toPickerDateString(new Date(new Date().getHours() >= 20 ? Date.now() + 86400000 : Date.now())),
      shipDateMenu: false,
      isOz: false,
      shippingService: '',
      signature: '',
      signatureCode: '',
      packageContent: 'commodities',
      shipmentPurpose: shipmentPurposes[0].value,
      bookingConfirmationNumber: '00000',
      labelStockType: 'STOCK_4X6',
      isOpenImportPackagesDialog: false,
      batteryLabel: '',
      freightCharges: '',
      autoGetRate: false,
      carriers: [],
      allCarriers: ['UPS', 'FEDEX'],
      isLithiumBattery: false,
      isPrintSafetyDocument: false,
      forceExpandState: null,
      reference: ''
    }
  },
  mixins: [checkRules, timeTools, measurementTools],
  mounted () {
    let defaultShippingService = (this.shippingServices.find((serviceType) => serviceType.default) || {}).value || ''
    const {shipmentPurpose, shippingService, bookingConfirmationNumber, batteryLabel, freightCharges} = this.$attrs
    const {note, memo, signature} = this.noteMemoSignature
    if (shipmentPurpose) this.shipmentPurpose = shipmentPurpose
    if (bookingConfirmationNumber) this.bookingConfirmationNumber = bookingConfirmationNumber
    if (batteryLabel) this.batteryLabel = batteryLabel
    if (freightCharges) this.freightCharges = freightCharges
    if (shippingService) defaultShippingService = shippingService
    this.setShippingService(defaultShippingService)
    if (!this.packages.length) {
      this.$emit('update:packages', [cloneDeep(initPackage)])
    }
    if (note || memo || signature) Object.assign(this, this.noteMemoSignature)
    this.initializePackageFields()
  },
  computed: {
    referenceOptions () {
      return [
        { text: 'department number', value: 'dept' },
        { text: 'invoice number', value: 'inv' },
        { text: 'po number', value: 'po' }
      ]
    },
    declaredValueRules () {
      if (this.isEeiMode) return []
      return [
        this.fieldIsRequired('value'),
        this.fieldIsNoLessThanZero('value')
      ]
    },
    qtyRules () {
      if (this.isEeiMode) return []
      return [
        this.fieldIsRequired('qty'),
        this.fieldIsOverZero('qty'),
        this.fieldIsInteger('qty')
      ]
    },
    weightRules () {
      if (this.isEeiMode) return []

      const defaultRules = [
        this.fieldIsRequired('weight'),
        this.fieldIsNoLessThanZero('weight')
      ]
      if (!this.isMeasurementMetric) {
        defaultRules.push(this.fieldIsPkgWeight(this.isOz))
      }
      return defaultRules
    },
    lengthRules () {
      if (this.isEeiMode) return []
      return [
        this.fieldIsRequired('length'),
        this.fieldIsOverZero('length')
      ]
    },
    widthRules () {
      if (this.isEeiMode) return []
      return [
        this.fieldIsRequired('width'),
        this.fieldIsOverZero('width')
      ]
    },
    heightRules () {
      if (this.isEeiMode) return []
      return [
        this.fieldIsRequired('height'),
        this.fieldIsOverZero('height')
      ]
    },
    isImportToUsByDHL () {
      return this.currentShippingService.carrier === 'dhl' && this.isImportToUS
    },
    allIcon () {
      if (this.carriers.length === this.allCarriers.length) return 'check_box'
      else if (this.carriers.length > 0) return 'indeterminate_check_box'
      return 'check_box_outline_blank'
    },
    batteries () {
      return [
        { text: this.$t('nonBattery'), value: '' },
        { text: `${this.$t('builtInBattery')}(966)`, value: '966' },
        { text: `${this.$t('externalBattery')}(967>2)`, value: '967>2' }
      ]
    },
    lithiumBatteryOptionItems () {
      return [
        {
          value: METAL_CONTAINED,
          label: this.$t('metalContainedInEquipment') || 'Lithium Metal Batteries (Section II) - 9',
          tooltip: this.$t('metalContainedTooltip')
        },
        {
          value: METAL_PACKED,
          label: this.$t('metalPackedWithEquipment') || 'Lithium Metal Batteries - Fully Regulated',
          tooltip: this.$t('metalPackedTooltip')
        },
        {
          value: ION_CONTAINED,
          label: this.$t('ionContainedInEquipment') || 'Lithium Ion Batteries (Section II) - 9',
          tooltip: this.$t('ionContainedTooltip')
        },
        {
          value: ION_PACKED,
          label: this.$t('ionPackedWithEquipment') || 'Lithium Ion Batteries - Fully Regulated',
          tooltip: this.$t('ionPackedTooltip')
        }
      ]
    },
    isShowBookingNumber () {
      return ['INTERNATIONAL_ECONOMY_FREIGHT', 'INTERNATIONAL_PRIORITY_FREIGHT'].includes(this.shippingService)
    },
    totalPkgInfo () {
      return this.packages.reduce((obj, pkg) => {
        obj.qty += pkg.qty
        obj.weight += pkg.weightLb * pkg.qty
        return obj
      }, {
        qty: 0,
        weight: 0
      })
    },
    headers () {
      const { enableReferences } = this.currentShippingService
      const { qty, weight } = this.totalPkgInfo
      const headers = [
        { text: this.$t('actions'), value: 'action', align: 'center', sortable: false },
        { text: `${this.$t('identicalPackageQty')}(${qty})`, value: '', align: 'left', sortable: false },
        this.isMeasurementMetric ?
          { text: `${this.$t('weight')}(${toMoney(weight)}${this.weightUnit})`, value: 'weight', align: 'left', sortable: false } :
          { text: `${this.$t('weight')}(${toMoney(weight)}${this.weightUnit})(${this.isOz ? `${this.$t('clickHereSwitchTo')}Lbs` : `${this.$t('clickHereSwitchTo')}Oz`})`, value: 'weight', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, value: 'packaging', align: 'left', sortable: false, width: '25%' },
        { text: this.$t('declaredValue'), value: 'createTime', align: 'left', sortable: false },
        { text: this.$t('memo'), value: 'memo', align: 'left', sortable: false, width: '15%' }
      ]
      if (enableReferences) {
        headers.push(
          { text: this.$t('memo'), value: 'reference', align: 'left', sortable: false, width: 300 }
        )
      }
      return headers
    },
    weightUnit () {
      let unit = 'weight'
      if (!this.isMeasurementMetric) {
        unit = this.isOz ? 'Oz' : 'Lbs'
      }
      return unit
    },
    signatureOptions () {
      return this.isAutoGetRate && this.autoGetRate
        ? [
          {
            text: 'No signature',
            value: 'NO_SIGNATURE_REQUIRED'
          },
          {
            text: 'Direct',
            value: 'DIRECT'
          },
          {
            text: 'Adult',
            value: 'ADULT'
          }
        ]
        : this.serviceSetting.signatureOptions
    },
    packagings () {
      return this.$store.getters.tenantPackagingsWithWarehousePackagings
    },
    templates () {
      return this.$store.getters.templates
    },
    labelServices () {
      return this.isImportToUS
        ? this.$store.getters.importToUSLabelServices
        : this.$store.getters.internationalLabelServices
    },
    shippingServices () {
      return this.labelServices
        .reduce((services, {carrier = '', enableInsured = false, serviceTypes = []}, idx) => [
          ...services,
          ...serviceTypes,
          { divider: true }
        ], [])
    },
    carrier () {
      return (this.labelServices.find(service =>
        service.serviceTypes.find(serviceType =>
          this.shippingService === serviceType.value ||
          this.serviceType === service.carrier
        )
      ) || {}).carrier
    },
    shipmentPurposeItems () {
      return shipmentPurposes
    },
    flexibleVars () {
      return this.$store.getters.labelTemplateFlexibleVars
    },
    isMeasurementMetric () {
      return !!this.$store.getters.isMeasurementMetric
    },
    sizeSuffix () {
      return this.isMeasurementMetric ? 'cm' : '"'
    },
    serviceSetting () {
      return this.$store.getters.getLabelServiceSettingByCarrierAndServiceTypeValue(this.carrier, this.shippingService)
    },
    currentShippingService () {
      return this.labelServices.find(service =>
        service.serviceTypes.some(serviceType => this.shippingService === serviceType.value)
      ) || {}
    },
    labelStockTypes () {
      return this.$store.getters.labelStockTypes || []
    },
    isSupportLabelStockType () {
      // 目前都需要支持
      // this.currentShippingService.isSupportLabelStockType
      return true
    },
    isLithiumBatteryDangerousGoods () {
      return this.dangerousGoods === 'LITHIUM_BATTERY'
    }
  },
  watch: {
    packages: {
      handler (newVal) {
        const targetPkg = newVal.find(pkg => pkg.po || pkg.dept || pkg.inv)
        if (targetPkg) {
          const reference = this.referenceOptions.find(option => targetPkg[option.value])?.value || ''
          this.reference = reference
        }
        if (newVal && Array.isArray(newVal)) {
          this.initializePackageFields(newVal)
          newVal.forEach(pkg => {
            if (pkg.lithiumBatteryOptions && pkg.lithiumBatteryOptions.length > 0) {
              if (pkg.isLithiumBattery !== true) {
                this.$set(pkg, 'isLithiumBattery', true)
              }
            } else if (pkg.lithiumBatteryOptions && pkg.lithiumBatteryOptions.length === 0 && this.dangerousGoods !== 'LITHIUM_BATTERY') {
              if (pkg.isLithiumBattery !== false) {
                this.$set(pkg, 'isLithiumBattery', false)
              }
            }
          })
        }
      },
      deep: true,
      immediate: true
    },
    // signatureOptions (value) {
    //   let defaultValue = value[0] ? value[0].value || '' : ''
    //   for (let option of value) {
    //     if (option.default) {
    //       defaultValue = option.value
    //       break
    //     }
    //   }
    //   this.signature = defaultValue
    // },
    shippingService (value, oldValue) {
      if (value === oldValue) return
      this.$emit('update:shippingService', value)
      if (!this.isSupportLabelStockType) this.labelStockType = 'STOCK_4X6'

      const isSupportDangerousGoods = this.labelServices.some(service => {
        const labelService = service.serviceTypes.find(type => type.value === this.shippingService)
        return labelService
          ? labelService.hasOwnProperty('isSupportDangerousGoods')
            ? labelService.isSupportDangerousGoods
            : service.isSupportDangerousGoods
          : false
      })
      if (!isSupportDangerousGoods) {
        this.$emit('clearDangerousGoods')
        this.packages.forEach(pkg => {
          pkg.isLithiumBattery = false
          pkg.lithiumBatteryOptions = []
        })
      }
    },
    autoGetRate (value) {
      this.signature = ''

      if (value) {
        this.carriers = this.allCarriers.map(item => item)
      } else {
        this.carriers = []
      }
      this.$emit('changeAutoGetRate', value)
    },
    dangerousGoods: {
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        if (newVal === 'LITHIUM_BATTERY') {
          this.forceExpandState = 'expand'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.packages.forEach((pkg, index) => {
                const iconId = `expand-icon-${pkg._key || index}`
                const icon = document.getElementById(iconId)
                if (icon) icon.click()
              })
              this.$nextTick(() => {
                this.forceExpandState = null
              })
            })
          })
        } else if (oldVal === 'LITHIUM_BATTERY' && newVal !== 'LITHIUM_BATTERY') {
          this.forceExpandState = 'collapse'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.packages.forEach((pkg, index) => {
                pkg.isLithiumBattery = false
                pkg.lithiumBatteryOptions = []
                const iconId = `expand-icon-${pkg._key || index}`
                const icon = document.getElementById(iconId)
                if (icon) icon.click()
              })
              this.$nextTick(() => {
                this.forceExpandState = null
              })
            })
          })
        }
      },
      immediate: true
    }
  },
  methods: {
    applyAllLithiumBattery (item) {
      this.packages.forEach(pkg => {
        this.$set(pkg, 'lithiumBatteryOptions', [...item.lithiumBatteryOptions])
        this.$set(pkg, 'isLithiumBattery', item.isLithiumBattery)
      })
    },
    toggleExpand (props) {
      if (this.forceExpandState === 'expand') {
        props.expanded = true
      } else if (this.forceExpandState === 'collapse') {
        props.expanded = false
      } else {
        props.expanded = !props.expanded
      }
    },
    initializePackageFields (packages = this.packages) {
      packages.forEach((pkg, index) => {
        if (!pkg._key) {
          this.$set(pkg, '_key', `pkg_${Date.now()}_${index}`)
        }
        if (pkg.isLithiumBattery === undefined) {
          this.$set(pkg, 'isLithiumBattery', false)
        }
        if (!pkg.lithiumBatteryOptions) {
          this.$set(pkg, 'lithiumBatteryOptions', [])
        }
        // isPrintSafetyDocument and signatureCode are now global (in HazardousCommodities)
      })
    },
    selectAll () {
      this.carriers = this.carriers.length === this.allCarriers.length
        ? []
        : this.allCarriers.map(item => item)
    },
    shipmentPurposeValid (isGetRateAction) {
      return v => {
        if (this.isAutoGetRate && this.autoGetRate && isGetRateAction) {
          return false
        }
        return !!v || this.$t('fieldIsRequired')
      }
    },
    arrayLengthRule () {
      return v => (v && v.length > 0) || this.$t('arrayLengthRule')
    },
    isObject,
    addPackage () {
      const newPackages = [...this.packages, cloneDeep(initPackage)]
      this.initializePackageFields(newPackages)
      this.$emit('update:packages', newPackages)
    },
    loadDimensionsData (packageDimensions) {
      if (packageDimensions.length === 0) return
      let arrayData = []
      for (let dimension of packageDimensions) {
        let entity = {
          qty: dimension.Quantity,
          weightLb: dimension.Weight,
          height: dimension.Height,
          width: dimension.Width,
          length: dimension.Length,
          insuredValue: dimension.insuredValue
        }
        arrayData.push(entity)
      }
      this.initializePackageFields(arrayData)
      this.$emit('update:packages', arrayData)
    },
    onSelectPackaging (e, item) {
      const {height, length, width, weight, isMeasurementMetric = false} = e
      Object.assign(item, this.convertPackagingSize({height, length, width}, isMeasurementMetric))
      if (weight) {
        this.$set(item, 'weightLb', this.convertWeight(weight, isMeasurementMetric))
      }
    },
    loadPackagesFromOldFormat (template) {
      const {
        packaging,
        insuredValue,
        weightLb,
        length: templateLength,
        width: templateWidth,
        height: templateHeight,
        isMeasurementMetric: isWeightMeasurementMetric = false
      } = template

      const targetPackaging = this.packagings.find(item => item.id === packaging) || {}
      const {
        height: pkgHeight,
        length: pkgLength,
        width: pkgWidth,
        isMeasurementMetric: isPackagingMeasurementMetric = false,
        ...rest
      } = targetPackaging

      return [{
        ...this.convertPackagingSize({
          height: pkgHeight || templateHeight,
          length: pkgLength || templateLength,
          width: pkgWidth || templateWidth
        }, isPackagingMeasurementMetric),
        ...rest,
        weightLb: this.convertWeight(weightLb, isWeightMeasurementMetric),
        qty: 1,
        declaredValue: insuredValue || 0
      }]
    },
    loadPackagesFromNewFormat (template) {
      const {packages, isMeasurementMetric: isWeightMeasurementMetric = false} = template
      return packages.map(pkg => ({
        ...pkg,
        weightLb: this.convertWeight(pkg.weight || 0, isWeightMeasurementMetric),
        qty: pkg.qty || 1,
        declaredValue: pkg.insuredValue || 0
      }))
    },
    onSelectTemplate (e) {
      let {
        shippingService,
        signature,
        memo = '',
        packages
      } = e

      const availableServices = (this.shippingServices || []).filter(item => !item.divider)
      const hasMatchedService = availableServices.some(item => item.value === shippingService)
      if (!hasMatchedService) {
        shippingService = ''
        signature = ''
      }

      let replacedMemo = this.flexibleVars.reduce((acc, variable) => {
        return acc.replace(new RegExp(`#{${variable}}`, 'g'), this[variable] || '')
      }, memo)
      Object.assign(this, {
        shippingService,
        memo: replacedMemo,
        signature
      })
      this.setShippingService(shippingService)
      let normalizedPackages
      if (packages && Array.isArray(packages) && packages.length > 0) {
        normalizedPackages = this.loadPackagesFromNewFormat(e)
      } else {
        normalizedPackages = this.loadPackagesFromOldFormat(e)
      }
      this.initializePackageFields(normalizedPackages)
      this.$emit('update:packages', normalizedPackages)
    },
    $$extractData (isGetRate = false) {
      const packages = this.packages
        .map(pkg => {
          pkg['insuredValue'] = pkg['declaredValue']

          const {
            packaging,
            qty,
            id,
            name,
            isPackagingDefault,
            weightLb,
            ...rest
          } = pkg
          return new Array(qty).fill(1).map(() => {
            return {...rest, weight: this.isOz ? (weightLb / 16) : weightLb, id, isMeasurementMetric: this.isMeasurementMetric}
          })
        })
        .flat()
        .map(({ reference, ...item }, index) => {
          const payload = {...item, sequenceNumber: index + 1}
          if (this.currentShippingService.enableReferences && this.reference) {
            payload[this.reference] = reference
          }
          return payload
        })

      const {
        shipDate,
        shippingService,
        signature,
        memo,
        note,
        bookingConfirmationNumber,
        shipmentPurpose,
        carrier,
        isImportToUsByDHL,
        batteryLabel,
        freightCharges,
        carriers
      } = this

      let replacedMemo = this.flexibleVars.reduce((acc, variable) => {
        if (variable !== 'quantity') {
          return acc
        }
        return acc.replace(new RegExp(`#{${variable}}`, 'g'), this.packages.reduce((pkg) => acc + pkg.qty, 0))
      }, memo)
      let purpose = shipmentPurpose
      const payload = {
        packages,
        shipDate,
        shippingService,
        signature,
        memo: replacedMemo,
        note,
        bookingConfirmationNumber,
        purpose,
        carrier,
        labelStockType: this.labelStockType
      }
      if (isImportToUsByDHL) {
        payload.batteryLabel = batteryLabel
        payload.freightCharges = freightCharges
      }
      if (isGetRate && this.isAutoGetRate && this.autoGetRate) {
        payload.carriers = carriers
      }
      return payload
    },
    setShippingService (value) {
      this.shippingService = value
      this.$emit('update:shippingService', value)
    },
    deletePackage (item) {
      this.$emit('update:packages', this.packages.filter(pkg => pkg !== item))
    }
  },
  props: {
    packages: Array,
    noteMemoSignature: Object,
    isImportToUS: Boolean,
    isAutoGetRate: Boolean,
    autoGetRateShippingService: String,
    isGetRateAction: Boolean,
    isEeiMode: {
      type: Boolean,
      default: false
    },
    dangerousGoods: {
      type: String,
      default: ''
    }
  }
}
</script>

<style scoped>
.table-item {
  padding-bottom: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/EeiPackageWidget.vue